// Island hydration — runs in the browser after the server-rendered page loads.
// Finds [data-island] elements, imports the matching component module, and
// mounts it with the serialized props so signals become reactive.

import { createRoot } from './signal.js';

/**
 * A component's client-side default export. Ordinarily a plain function that
 * returns a `Node` (a signal-driven island, per `client/jsx-runtime.ts`) — `hydrate()`
 * runs it inside a `createRoot()` and inserts the returned Node itself.
 *
 * A component may instead attach an optional `mount()` that takes over the entire
 * mount lifecycle — inserting into `container` itself and returning its own dispose
 * function — for component systems whose lifecycle isn't "call a function, get a
 * Node back" (e.g. a future non-signal-based bridge with its own reconciler/root
 * concept). When `mount` is present, `hydrate()` defers to it entirely and does not
 * apply the slot-restoration behavior described below.
 */
export type MountableComponent<P extends Record<string, unknown> = Record<string, unknown>> =
  ((props: P) => Node) & {
    mount?: (container: Element, props: P) => () => void;
  };

export interface IslandManifest {
  [islandId: string]: () => Promise<{ default: MountableComponent }>;
}

// Per-island reactive-scope teardown, keyed on the mounted [data-island] element.
// hydrate() populates this; disposeIsland() (and future consumers — lazy-hydration
// scheduling, Islands HMR) read it.
const islandDisposers = new WeakMap<Element, () => void>();

/**
 * Tear down a hydrated island's reactive scope — runs every `onCleanup()` handler
 * and stops its effects from responding to further signal changes. No-op if `el`
 * was never hydrated or has already been disposed.
 */
export function disposeIsland(el: Element): void {
  islandDisposers.get(el)?.();
  islandDisposers.delete(el);
}

export async function hydrate(manifest: IslandManifest): Promise<void> {
  const islands = document.querySelectorAll<HTMLElement>('[data-island]');

  await Promise.all(
    Array.from(islands).map(async (el) => {
      const id = el.dataset.island;
      if (!id || !(id in manifest)) return;

      let props: Record<string, unknown> = {};
      try {
        props = JSON.parse(el.dataset.props ?? '{}');
      } catch {
        console.warn(`[davaux] Failed to parse props for island "${id}"`);
      }

      // Capture server-rendered slot content before mounting overwrites it.
      // Components mark non-serializable children slots with data-island-slot
      // so the server HTML can be restored into the hydrated DOM tree.
      const slotSnapshots: Node[][] = [];
      el.querySelectorAll('[data-island-slot]').forEach((slot) => {
        slotSnapshots.push([...slot.childNodes].map((n) => n.cloneNode(true)));
      });

      const { default: Component } = await manifest[id]();

      // A component with its own mount() takes over the whole mount lifecycle —
      // it inserts into `el` itself and owns its own dispose. Slot-restoration
      // (below) is skipped: it's specific to the createRoot()-mounted path.
      if (Component.mount) {
        const dispose = Component.mount(el, props);
        islandDisposers.set(el, dispose);
        return;
      }

      // Run the component inside a reactive root so any onCleanup() calls
      // (e.g. from createResource refetchInterval) are collected and can be
      // disposed if the island is ever unmounted — see disposeIsland().
      let dispose!: () => void;
      const node = createRoot((d) => {
        dispose = d;
        return Component(props);
      });

      // Guard: in dev, the island bundle may lag behind the server bundle by
      // one rebuild cycle. If the component returns Promise<string> instead of
      // a DOM Node, calling el.replaceChildren(Promise) would stringify the
      // Promise to "[object Promise]" via Web IDL coercion. Skip hydration so
      // the server-rendered HTML is preserved until the bundle is current.
      if (node instanceof Promise) {
        console.warn(
          `[davaux] Island "${id}" returned a Promise instead of a DOM Node — island bundle may be stale. Server HTML preserved.`,
        );
        dispose(); // nothing was mounted — tear down the aborted root immediately
        return;
      }

      islandDisposers.set(el, dispose);

      // Replace server-rendered children with the live DOM subtree
      el.replaceChildren(node);

      // Re-inject server-rendered children into any slot elements whose client
      // render produced no meaningful content (e.g. because children was a
      // non-serializable OmlNode Promise that became {} in JSON and rendered
      // as the text "[object Object]").
      if (slotSnapshots.length > 0) {
        // children serialized as {} means a non-serializable value (Promise /
        // OmlNode) — the client can't reproduce the SSR output, so we must use
        // the captured snapshot for any marked slots.
        const c = props.children;
        const childrenNonSerializable =
          c !== null &&
          c !== undefined &&
          typeof c === 'object' &&
          !Array.isArray(c) &&
          Object.keys(c as Record<string, unknown>).length === 0;
        if (childrenNonSerializable) {
          let i = 0;
          el.querySelectorAll('[data-island-slot]').forEach((slot) => {
            const captured = slotSnapshots[i++];
            if (captured?.length) slot.replaceChildren(...captured);
          });
        }
      }
    }),
  );
}
